/**
 * A pill with two or more options side by side; the picked one gets a filled
 * white chip. Used for both the period picker and the accounts picker.
 */

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  /** Name screen readers announce for the whole group */
  label: string;
  /** Each option's value, on-screen text, and optional screen-reader name */
  options: { value: T; label: string; name?: string }[];
  /** The option currently picked */
  value: T;
  /** Called with the option the user clicks */
  onChange: (next: T) => void;
}) {
  return (
    // Grey pill holding the options; screen readers read it as a radio group
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-full bg-si-surface-2 p-0.5">
      {options.map((o) => {
        // True for the option that is currently picked
        const selected = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={o.name}
            onClick={() => onChange(o.value)}
            // Picked option: white chip with a small shadow; others: grey text
            className={`min-h-7 rounded-full px-3 text-si-sm font-semibold transition-colors ${
              selected ? "bg-si-bg text-si-ink shadow-[0_1px_2px_rgba(0,0,0,.15)]" : "text-si-ink-3 hover:text-si-ink"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
